import type { IGroup, IImage, ILayer, INode, IRenderer } from './backend/IRenderer'
import { CloudFile } from '@/lib/CloudFile'
import { isApp } from '@/index'
import { Floorplan } from '@/app/data/type'
import { Network } from '@capacitor/network'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'
import { TILE_DIR } from '@/app/constant'

/**
 * 타일 기반 배경 레이어
 * - 줌 레벨에 따라 적절한 해상도의 타일을 동적으로 로드
 * - 보이지 않는 타일은 자동으로 언로드
 */
export class TileLayer {
  tileConfig: NonNullable<Floorplan['tile_config']>
  private tileBaseUrl: string
  private renderer: IRenderer
  private container: ILayer
  private tileGroups: Map<number, IGroup> = new Map()
  private loadedTiles: Map<string, IImage> = new Map()
  private currentScale: number = 1
  private rotate: number = 0
  private baseWidth: number // 기본 도면 크기
  private baseHeight: number

  constructor(
    options: Pick<Floorplan, 'tile_config' | 'tile_base_url'>,
    renderer: IRenderer,
    rotate: number = 0,
  ) {
    this.tileConfig = options.tile_config!
    this.tileBaseUrl = options.tile_base_url!
    this.renderer = renderer
    // 타일은 배경 레이어(엔티티와 같은 레이어)의 맨 아래에 레벨별 그룹으로 붙는다
    this.container = renderer.layers.background

    // 회전 처리
    this.rotate = ((rotate % 360) + 360) % 360

    // 기본 도면 크기 계산
    const maxScale = Math.max(...this.tileConfig.levels.map(l => l.scale))
    this.baseWidth = this.tileConfig.bounds.width / maxScale
    this.baseHeight = this.tileConfig.bounds.height / maxScale

    // 각 레벨별 그룹 생성
    this.tileConfig.levels.forEach(level => {
      const group = this.renderer.createGroup({
        visible: false,
        listening: false,
        // 스케일 적용 (기본도면과 1:1로 맞추기위해 타일을 축소)
        scaleX: 1 / level.scale,
        scaleY: 1 / level.scale,
        // 성능을 위해 타일은 alpha 채널이 없음.. 따라서 잉여공간을 마스킹함
        clip: {
          x: 0,
          y: 0,
          width: this.baseWidth * level.scale,
          height: this.baseHeight * level.scale,
        },
      })

      // 그룹에 기본 도면과 동일한 회전 및 변환을 적용
      if (this.rotate === 90) {
        group.rotate(90)
        group.x(this.baseHeight)
        group.y(0)
      } else if (this.rotate === 180) {
        group.rotate(180)
        group.x(this.baseWidth)
        group.y(this.baseHeight)
      } else if (this.rotate === 270) {
        group.rotate(270)
        group.x(0)
        group.y(this.baseWidth)
      }

      this.container.moveToBottom()
      this.container.add(group)
      this.tileGroups.set(level.scale, group)
    })
  }

  // 현재 뷰포트에 맞는 타일들을 로드
  async loadVisibleTiles(
    viewport: { x: number; y: number; width: number; height: number },
    zoom: number,
  ): Promise<void> {
    // 현재 줌 레벨에 맞는 스케일 선택
    const newScale = this.findCurrentScale(zoom)

    // scale 1 이하에서는 타일을 사용하지 않음
    if (newScale <= 1) {
      this.tileGroups.forEach(group => {
        if (group.visible()) this.fade(group, 0, () => group.visible(false))
      })
      this.currentScale = 1
      return
    }

    const previousScale = this.currentScale
    const scaleChanged = newScale !== previousScale
    this.currentScale = newScale

    const level = this.tileConfig.levels.find(l => l.scale === this.currentScale)
    if (!level) {
      console.error('레벨 없음:', this.currentScale)
      return
    }

    const tileSize = this.tileConfig.tileSize
    const tileViewport = this.transformViewportToTile(viewport, level.scale)

    const startCol = Math.max(0, Math.floor(tileViewport.x / tileSize))
    const endCol = Math.min(
      level.cols - 1,
      Math.floor((tileViewport.x + tileViewport.width) / tileSize),
    )
    const startRow = Math.max(0, Math.floor(tileViewport.y / tileSize))
    const endRow = Math.min(
      level.rows - 1,
      Math.floor((tileViewport.y + tileViewport.height) / tileSize),
    )

    const tilesToLoad: Array<{ col: number; row: number }> = []
    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        const tileKey = `${this.currentScale * 100}_${col}_${row}`
        if (!this.loadedTiles.has(tileKey)) {
          tilesToLoad.push({ col, row })
        }
      }
    }

    // 타일 로드 완료 후 시각적 전환
    if (tilesToLoad.length > 0) await this.loadTiles(tilesToLoad, this.currentScale)

    if (scaleChanged) {
      const oldGroup = this.tileGroups.get(previousScale)
      const targetGroup = this.tileGroups.get(newScale)

      if (targetGroup) {
        targetGroup.opacity(0)
        targetGroup.visible(true)
        this.fade(targetGroup, 1, () => {
          // 새 그룹 완전 표시 후 → 구 그룹 숨기고 타일 정리
          this.tileGroups.forEach((group, scale) => {
            if (scale !== this.currentScale && group.visible()) {
              group.visible(false)
            }
          })
          this.cleanupNonCurrentTiles()
        })
      } else if (oldGroup) {
        this.fade(oldGroup, 0, () => oldGroup.visible(false))
      }
    }

    this.unloadInvisibleTiles(viewport)
  }

  // 타일들 로드
  private async loadTiles(
    tiles: Array<{ col: number; row: number }>,
    scale: number,
  ): Promise<void> {
    const { tileSize, floorplanId, createdAt } = this.tileConfig
    const group = this.tileGroups.get(scale)
    if (!group) return

    const loadPromises = tiles.map(async ({ col, row }) => {
      const tileKey = `${scale * 100}_${col}_${row}`
      const tileUrl = `${this.tileBaseUrl}/x${scale}/${floorplanId}_${tileKey}_${createdAt}.webp`

      try {
        const actualUrl = await this.convertStorageUrl(tileUrl)
        let image: IImage
        try {
          image = await this.renderer.loadImage(actualUrl)
        } catch (error) {
          console.warn(`[TileLayer] 타일 로드 실패: ${tileUrl}`, error)
          throw error
        }
        // 타일 원본 좌표 (확대된 타일 이미지 기준)
        const tileX = col * tileSize
        const tileY = row * tileSize

        image.width(tileSize)
        image.height(tileSize)
        image.listening(false)

        image.x(tileX)
        image.y(tileY)

        group.add(image)
        this.loadedTiles.set(tileKey, image)

        // 타일 페이드인
        this.fade(image, 1)
      } catch (error) {
        // 에러는 경고만 출력하고 계속 진행
        console.warn(`[TileLayer] 타일 로드 에러: ${tileUrl}`, error)
      }
    })

    await Promise.all(loadPromises)
  }

  // 뷰포트 밖의 타일 언로드
  private unloadInvisibleTiles(viewport: {
    x: number
    y: number
    width: number
    height: number
  }): void {
    const tileSize = this.tileConfig.tileSize

    if (this.currentScale <= 1) {
      // 활성 타일이 없으면 모든 타일 언로드 (그룹이 이미 페이드아웃되므로 즉시 제거)
      this.loadedTiles.forEach((image, key) => {
        image.destroy()
        this.loadedTiles.delete(key)
      })
      return
    }

    const tileViewport = this.transformViewportToTile(viewport, this.currentScale)

    const margin = tileSize * 2

    const expandedViewport = {
      x: tileViewport.x - margin,
      y: tileViewport.y - margin,
      width: tileViewport.width + margin * 2,
      height: tileViewport.height + margin * 2,
    }

    this.loadedTiles.forEach((image, key) => {
      const [scaleStr, colStr, rowStr] = key.split('_')
      const scale = parseInt(scaleStr) / 100
      const col = parseInt(colStr)
      const row = parseInt(rowStr)

      // 현재 스케일이 아닌 타일은 전환 콜백에서 일괄 정리
      if (scale !== this.currentScale) return

      // 타일 좌표 (원본 타일 좌표계 기준)
      const tileX = col * tileSize
      const tileY = row * tileSize

      // 뷰포트 밖이면 즉시 언로드
      // (화면 밖이라 페이드가 보이지 않는데, 트윈이 타일마다 0.3초간 배경 레이어 전체
      //  — 전 엔티티 포함 — 를 매 프레임 다시 그리게 해 저사양에서 조작 지연을 유발했음)
      if (
        tileX + tileSize < expandedViewport.x ||
        tileX > expandedViewport.x + expandedViewport.width ||
        tileY + tileSize < expandedViewport.y ||
        tileY > expandedViewport.y + expandedViewport.height
      ) {
        image.destroy()
        this.loadedTiles.delete(key)
      }
    })

    this.container.batchDraw() // 타일 로드/언로드 후 일괄 드로잉
  }

  private readonly STORAGE_DIRECTORY = Directory.Cache

  /**
   * storage:// URL을 실제 접근 가능한 URL로 변환
   * - 웹: signed URL
   * - 앱: 로컬 파일 우선, 없으면 signed URL
   */
  private async convertStorageUrl(storageUrl: string): Promise<string> {
    // 웹은 항상 HTTP로
    if (!isApp()) return CloudFile.ResourceUrlToSignedUrl(storageUrl)

    // 앱: 로컬 파일 경로 추출
    const filePath = this.extractFilePath(storageUrl)

    // 로컬 파일 존재 확인
    try {
      await Filesystem.stat({
        path: filePath,
        directory: this.STORAGE_DIRECTORY,
      })

      // 파일 URI 얻기
      const result = await Filesystem.getUri({
        path: filePath,
        directory: this.STORAGE_DIRECTORY,
      })

      // 웹뷰에서 접근 가능한 URL로 변환
      return Capacitor.convertFileSrc(result.uri)
    } catch (error) {
      // 로컬 파일 없음, HTTP로 fallback
      console.info('[TileLayer] Local file not found, trying HTTP:', storageUrl, error)
    }

    // 로컬 파일 없으면 네트워크 확인 후 HTTP 시도
    const networkStatus = await Network.getStatus()
    // 인터넷 없음: 렌더링 안 함
    if (!networkStatus.connected) throw new Error(`No network for: ${storageUrl}`)
    // 인터넷 있으면 HTTP로 직접 로드
    return CloudFile.ResourceUrlToSignedUrl(storageUrl)
  }

  /**
   * storage:// URL에서 파일 경로 추출
   * 예: storage://homecheck-service/.../file.webp -> _tile/.../file.webp
   */
  private extractFilePath(storageUrl: string): string {
    // storage:// URL에서 파일명 추출
    const urlParts = storageUrl.split('/')
    const fileName = urlParts[urlParts.length - 1]
    const scale = urlParts[urlParts.length - 2] // x1, x2, x4 등

    return `${TILE_DIR}/${scale}/${fileName}`
  }

  // 줌 레벨에 맞는 스케일 선택
  private findCurrentScale(zoom: number): number {
    if (this.tileGroups.size === 0) return 1
    const scales = Array.from(this.tileGroups.keys()).sort((a, b) => a - b)
    let selectedScale = 1
    for (const s of scales) {
      if (zoom >= s) selectedScale = s
      else break
    }
    return selectedScale
  }

  // 뷰포트를 타일 좌표계로 변환
  private transformViewportToTile(
    viewport: { x: number; y: number; width: number; height: number },
    scale: number,
  ): { x: number; y: number; width: number; height: number } {
    // 뷰포트의 4개 꼭지점을 스테이지 좌표로
    const corners = [
      { x: viewport.x, y: viewport.y },
      { x: viewport.x + viewport.width, y: viewport.y },
      { x: viewport.x, y: viewport.y + viewport.height },
      { x: viewport.x + viewport.width, y: viewport.y + viewport.height },
    ]

    // 각 꼭지점을 스테이지 좌표에서 기본 도면 좌표로
    const worldCorners = corners.map(stageCorner => {
      let worldX: number
      let worldY: number

      const stageX = stageCorner.x
      const stageY = stageCorner.y

      if (this.rotate === 90) {
        worldX = stageY
        worldY = -stageX + this.baseHeight
      } else if (this.rotate === 180) {
        worldX = -stageX + this.baseWidth
        worldY = -stageY + this.baseHeight
      } else if (this.rotate === 270) {
        worldX = -stageY + this.baseWidth
        worldY = stageX
      } else {
        worldX = stageX
        worldY = stageY
      }
      return { x: worldX, y: worldY }
    })

    // 기본 도면좌표를 타일 좌표로 변환 (스케일)
    const tileCorners = worldCorners.map(worldCorner => {
      return {
        x: worldCorner.x * scale,
        y: worldCorner.y * scale,
      }
    })

    // 변환된 4개 꼭지점의 바운딩 박스
    const minX = Math.min(...tileCorners.map(p => p.x))
    const maxX = Math.max(...tileCorners.map(p => p.x))
    const minY = Math.min(...tileCorners.map(p => p.y))
    const maxY = Math.max(...tileCorners.map(p => p.y))

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    }
  }

  // 현재 스케일이 아닌 모든 타일 일괄 정리
  private cleanupNonCurrentTiles(): void {
    this.loadedTiles.forEach((image, key) => {
      const [scaleStr] = key.split('_')
      const tileScale = parseInt(scaleStr) / 100
      if (tileScale !== this.currentScale) {
        image.destroy()
        this.loadedTiles.delete(key)
      }
    })
  }

  // 페이드 애니메이션
  private fade(node: INode, targetOpacity: number, onComplete?: () => void): void {
    const DURATION = 0.3
    if (node.opacity() === targetOpacity) {
      onComplete?.()
      return
    }
    const animation = this.renderer.tween(
      node,
      { opacity: targetOpacity },
      {
        duration: DURATION,
        easing: 'easeInOut',
        onFinish: (): void => {
          onComplete?.()
        },
      },
    )
    animation.play()
  }

  // 모든 타일 정리
  destroy(): void {
    this.loadedTiles.forEach(image => image.destroy())
    this.loadedTiles.clear()
    this.tileGroups.forEach(group => group.destroy())
    this.tileGroups.clear()
  }

  // 타일 레이어의 전체 크기 반환
  getSize(): { width: number; height: number } {
    if (this.rotate === 90 || this.rotate === 270) {
      return {
        width: this.baseHeight,
        height: this.baseWidth,
      }
    }
    return {
      width: this.baseWidth,
      height: this.baseHeight,
    }
  }
}
